import React from "react";
import styled, { css } from "styled-components";

import Avatar from "../avatar/Avatar";

/**
 * 引用カード。「誰かの言葉」とその発話者を、映画レビューの引用のように
 * 重みをもって見せる。
 *
 * 公開プロフィールのステートメントセクション（`ProfileStatement`）をコンパクト
 * にした意匠で、構成・表示順序も同セクションと同じ:
 * アバター（任意）+ 引用アイコンを背景に敷いた本文の行 → 右下に引用線 +
 * 表示名 + 肩書き。背景は完全白 + ソフトな影。
 *
 * 使用箇所:
 * - GALLERY のアーティストパネル（アバターあり / クリックでプロフィールへ遷移 /
 *   ステージ採寸に合わせた高さ + 行クランプ）
 * - 公開プロフィールのレビューセクション（アバターなし / 全文表示）
 */
export interface QuoteCardProps {
  /** 引用本文。 */
  text: string;
  /**
   * 署名行の表示名。省略（または空）のときは署名行そのものを描画しない
   * （匿名レビュー等）。
   */
  name?: string;
  /** 署名行の肩書き。`name` がないときは表示されない。 */
  title?: string;
  /** アバターを出す場合のみ指定する。 */
  avatar?: {
    src?: string;
    /** イニシャル表示・代替テキスト用の名前。 */
    name: string;
    size: number;
  };
  /**
   * 本文の表示行数の上限。省略時はクランプせず全文を表示する。
   * 呼び出し側でカード高さを固定する場合に、同じ見積もり行数を渡す。
   */
  clampLines?: number;
  /** 指定するとカードをボタンにしてクリック可能にする。 */
  onClick?: () => void;
  /** カード高さ(px)。呼び出し側で採寸している場合に指定する。 */
  height?: number;
  /** `onClick` を指定したときのアクセシブルな名前。 */
  ariaLabel?: string;
  className?: string;
}

/**
 * カード本体の共通スタイル。背景は完全白 + ソフトな影で、周囲の要素から
 * 浮き立たせる。クリック可能かどうかで要素自体（div / button）を切り替える
 * ため、スタイルだけを切り出して両方で共有する。
 */
const cardBase = css`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 12px;
  padding: 16px 20px;
  border-radius: 4px;
  border: none;
  background: #fff;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08);
  appearance: none;
  margin: 0;
  text-align: left;
  overflow: hidden;
  color: #000;
`;

const StaticRoot = styled.div`
  ${cardBase}
`;

/** クリック可能なカード。ホバー・フォーカスのフィードバックを持つ。 */
const ButtonRoot = styled.button`
  ${cardBase}
  cursor: pointer;

  &:hover {
    box-shadow: 0 10px 32px rgba(0, 0, 0, 0.14);
  }

  &:focus-visible {
    outline: 2px solid var(--soremo-blue);
    outline-offset: 2px;
  }
`;

/** アバター + 本文の行（ProfileStatement の u-row 相当）。
 * gap 32px はアバターの wide 影が本文へ重ならないための余白。 */
const Body = styled.span`
  display: flex;
  align-items: center;
  gap: 32px;
  width: 100%;
`;

/** 本文のラッパー。背景の引用アイコンの位置基準になる。 */
const TextArea = styled.span`
  position: relative;
  flex: 1;
  min-width: 0;
`;

/**
 * 本文の左上に背景装飾として敷く開き引用アイコン（ProfileStatement と同じ
 * 流儀。format_quote は閉じ引用のグリフなので 180 度回転して使う）。
 */
const QuoteIcon = styled.span.attrs({
  className: "material-symbols-rounded",
})`
  position: absolute;
  top: -8px;
  left: -4px;
  font-size: 32px;
  color: var(--soremo-border);
  transform: rotate(180deg);
  pointer-events: none;
  user-select: none;
`;

/**
 * 引用本文。文字組みは本家 bodytext-quote と同じ letter-spacing 2.5px
 * （フォントサイズのみ 13px に縮小）。u-mt16 で引用アイコンとの重なりを避け、
 * relative で引用アイコンより前面に出す。`$clampLines` を渡した場合のみ
 * 指定行数でクランプする。
 *
 * 改行位置は本家 `ProfileStatement`（`StatementFitText`）と同じく尊重する。
 * カード高さを固定する呼び出し側は、行数の見積もりでも明示的な改行を
 * 数えること。
 */
const Text = styled.span.attrs({
  className: "bodytext-13 u-mt16",
})<{ $clampLines?: number }>`
  position: relative;
  width: 100%;
  letter-spacing: 2.5px;
  white-space: pre-wrap;

  ${({ $clampLines }) =>
    $clampLines
      ? css`
          overflow: hidden;
          display: -webkit-box;
          -webkit-line-clamp: ${$clampLines};
          -webkit-box-orient: vertical;
        `
      : ""}
`;

/** 右下の署名行（ProfileStatement の u-row-end + 引用線 + 名前 + 肩書き）。 */
const Footer = styled.span`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  width: 100%;
`;

/** 署名行の名前 + 肩書き（本家の u-row u-gap16 相当）。 */
const NameRow = styled.span`
  display: flex;
  align-items: center;
  gap: 16px;
  min-width: 0;
`;

/** 表示名（本家 heading のコンパクト版 heading-13）。 */
const Name = styled.span.attrs({
  className: "heading-13 u-line-height-150",
})`
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;

/** 肩書き（本家 bodytext-13 のコンパクト版 bodytext-11、1 行省略）。 */
const Title = styled.span.attrs({
  className: "bodytext-11 u-text-black u-line-height-150",
})`
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;

const QuoteCard: React.FC<QuoteCardProps> = ({
  text,
  name,
  title,
  avatar,
  clampLines,
  onClick,
  height,
  ariaLabel,
  className,
}) => {
  const showFooter = !!name?.trim();
  // カード内の要素は button の内容モデルに収まるよう、すべて span 系で組んでいる。
  const content = (
    <>
      <Body>
        {avatar && (
          <Avatar
            size={avatar.size}
            src={avatar.src}
            name={avatar.name}
            shadow="wide"
          />
        )}
        <TextArea>
          {text && (
            <>
              <QuoteIcon aria-hidden>format_quote</QuoteIcon>
              <Text $clampLines={clampLines}>{text}</Text>
            </>
          )}
        </TextArea>
      </Body>
      {showFooter && (
        <Footer>
          <span className="c-quote-line" />
          <NameRow>
            <Name>{name}</Name>
            {title && <Title>{title}</Title>}
          </NameRow>
        </Footer>
      )}
    </>
  );

  const style = height ? { height } : undefined;

  if (onClick) {
    return (
      <ButtonRoot
        type="button"
        className={className}
        style={style}
        onClick={onClick}
        aria-label={ariaLabel}
      >
        {content}
      </ButtonRoot>
    );
  }

  return (
    <StaticRoot className={className} style={style}>
      {content}
    </StaticRoot>
  );
};

export default QuoteCard;
